import { useState, useMemo } from 'react';
import styled from 'styled-components';
import { spacing } from 'ui/theme';
import Dropdown from 'core/dropdown';
import { NumberInput } from '@iterable/deprecated_react_components';
import { pxToRem } from 'utils/convert';

import { REFINE_DROPDOWN_OPTIONS } from './constants';

const StyledContainer = styled.div`
  display: inline-flex;
`;

const Wrapper = styled.div`
  padding-right: ${spacing.half};
`;

const TextWrapper = styled(Wrapper)`
  display: inline-flex;
  align-items: center;
`;

const NumberInputWrapper = styled(Wrapper)`
  input[type='text'] {
    width: ${spacing.x6};
  }
`;

const DropdownWrapper = styled(Wrapper)`
  &&&& {
    width: ${pxToRem(104)};
    font-weight: bold;
    > * {
      margin-bottom: 0;
    }
  }
`;

const ViewModePropertyRefinementContainer = styled.div`
  padding: ${spacing.half};
  padding-right: 0;
`;

// Used for getting the initial dropdown value from the minMatch and maxMatch values
const getDropdownValue = (minMatch, maxMatch, isExactEnabled) => {
  // both null is any
  if (!minMatch && !maxMatch) return REFINE_DROPDOWN_OPTIONS.ANY.value;
  // if they equal but not null its exactly
  if (minMatch === maxMatch && isExactEnabled) return REFINE_DROPDOWN_OPTIONS.EXACTLY.value;
  // else its between
  return REFINE_DROPDOWN_OPTIONS.BETWEEN.value;
};

// type PropertyRefinementOptionsProps = {
//   /** If query builder is in view mode. */
//   isViewMode?: boolean,
//   /** If query builder is in read only mode. */
//   isReadOnly?: boolean,
//   /** The current minMatch and maxMatch. */
//   value: string,
//   /** Function to set minMatch and maxMatch. */
//   handleChange: () => void,
// };

export const PropertyRefinementOptions = ({
  isViewMode = false,
  value,
  isReadOnly = false,
  handleChange,
  canSetMaxPropertyCount = true,
}) => {
  const { minMatch, maxMatch } = value ?? {};

  const [dropdownValue, setDropdownValue] = useState(
    getDropdownValue(minMatch, maxMatch, canSetMaxPropertyCount),
  );
  const [minMatchValue, setMinMatchValue] = useState(minMatch || 1);
  const [maxMatchValue, setMaxMatchValue] = useState(maxMatch || '');

  // Dropdown options
  const options = useMemo(() => {
    return Object.values(REFINE_DROPDOWN_OPTIONS).reduce((acc, o) => {
      // do not return `Exactly` if you cannot set max property count
      return o.value === REFINE_DROPDOWN_OPTIONS.EXACTLY.value && !canSetMaxPropertyCount
        ? acc
        : [...acc, o];
    }, []);
  }, [canSetMaxPropertyCount]);

  const onHandleChange = ({ newValue, min, max }) => {
    if (newValue) setDropdownValue(newValue);
    setMinMatchValue(min);
    if (canSetMaxPropertyCount) setMaxMatchValue(max);
    handleChange(min, max === '' ? null : max);
  };

  /**
   * handleDropdownChange
   * Callback function when changing the dropdown value.
   * @param { Object } e: The change event.
   * @param { Object } props. The props of the property dropdown containing the updated value.
   * @returns { void }
   */
  const handleDropdownChange = (e, { value: newValue }) => {
    // after setting dropdown value update min and max match values to defaults
    if (newValue === REFINE_DROPDOWN_OPTIONS.ANY.value) {
      onHandleChange({ newValue, min: null, max: null });
    } else if (newValue === REFINE_DROPDOWN_OPTIONS.BETWEEN.value) {
      onHandleChange({ newValue, min: 1, max: '' });
    } else if (newValue === REFINE_DROPDOWN_OPTIONS.EXACTLY.value && canSetMaxPropertyCount) {
      onHandleChange({ newValue, min: 1, max: 1 });
    }
  };

  /**
   * handleMinMatchChange
   * Callback function when changing the min match value.
   * @param { Object } e: The change event.
   * @param { Object } valuerProps. The props of the number containing the updated value.
   * @returns { void }
   */
  const handleMinMatchChange = (e, { value: newValue }) => {
    const isMinMatchExact = dropdownValue === REFINE_DROPDOWN_OPTIONS.EXACTLY.value;
    // [WF-948] we only want to set the value if it's within bounds
    if (newValue >= 1 && newValue <= 999) {
      const min = parseInt(newValue, 10);
      onHandleChange({ min, max: isMinMatchExact ? min : maxMatchValue });
    }
  };

  /**
   * handleMaxMatchChange
   * Callback function when changing the max match value.
   * @param { Object } e: The change event.
   * @param { Object } valuerProps. The props of the number containing the updated value.
   * @returns { void }
   */
  const handleMaxMatchChange = (e, { value: newValue }) => {
    // [WF-948] we only want to set the value if it's within bounds
    if (newValue >= 1 && newValue <= 999 && canSetMaxPropertyCount) {
      const max = parseInt(newValue, 10);
      onHandleChange({ min: minMatchValue, max });
    }
  };

  return isViewMode ? (
    <ViewModePropertyRefinementContainer data-test="query-builder-property-refinement-view-mode">
      {dropdownValue}
      {dropdownValue !== REFINE_DROPDOWN_OPTIONS.ANY.value && ` ${minMatchValue || '1'}`}
      {dropdownValue === REFINE_DROPDOWN_OPTIONS.BETWEEN.value && ` to ${maxMatchValue || 'All'}`}
    </ViewModePropertyRefinementContainer>
  ) : (
    <StyledContainer data-test="query-builder-property-refinement-options">
      <DropdownWrapper>
        <Dropdown
          data-test="query-builder-property-refinement-dropdown"
          disabled={isReadOnly}
          fluid
          options={options}
          search
          value={dropdownValue}
          onChange={handleDropdownChange}
        />
      </DropdownWrapper>

      {/* Show first number input if exactly or between is selected in dropdown */}
      {dropdownValue !== REFINE_DROPDOWN_OPTIONS.ANY.value && (
        <NumberInputWrapper>
          <NumberInput
            allowNegative={false}
            onChange={handleMinMatchChange}
            inline
            value={minMatchValue}
            disabled={isReadOnly}
            disableIncrement={minMatchValue >= 1000 || isReadOnly}
            disableDecrement={minMatchValue === 0 || isReadOnly}
          />
        </NumberInputWrapper>
      )}

      {/* Show second number input if between is selected in dropdown */}
      {dropdownValue === REFINE_DROPDOWN_OPTIONS.BETWEEN.value && (
        <>
          <TextWrapper>to</TextWrapper>
          <NumberInputWrapper>
            <NumberInput
              allowNegative={false}
              onChange={handleMaxMatchChange}
              inline
              value={maxMatchValue}
              disabled={isReadOnly || !canSetMaxPropertyCount}
              disableIncrement={maxMatchValue >= 1000 || isReadOnly}
              disableDecrement={maxMatchValue === 0 || isReadOnly}
              placeholder="All"
            />
          </NumberInputWrapper>
        </>
      )}
    </StyledContainer>
  );
};
